import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import DataTable, { Column } from "../components/DataTable";
import "../styles/auditTrail.css";

interface AuditTrailRecord {
  id: number;
  admin_user_id?: number | null;
  admin_name?: string | null;
  admin_email?: string | null;
  action: "ADD" | "EDIT" | "DELETE" | string;
  module_name: string;
  record_id?: string | number | null;
  record_label?: string | null;
  old_data?: any;
  new_data?: any;
  ip_address?: string | null;
  user_agent?: string | null;
  createdAt?: string;
  created_at?: string;
}

interface AdminUserOption {
  id: number | string;
  name?: string;
  username?: string;
  email?: string;
}

const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

const ACTION_OPTIONS = [
  { label: "All Actions", value: "all" },
  { label: "Add", value: "ADD" },
  { label: "Edit", value: "EDIT" },
  { label: "Delete", value: "DELETE" },
];

const getArrayFromResponse = (result: any): any[] => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.rows)) return result.rows;
  if (Array.isArray(result?.users)) return result.users;
  if (Array.isArray(result?.data?.rows)) return result.data.rows;
  if (Array.isArray(result?.data?.users)) return result.data.users;
  return [];
};

const normalizeAction = (action: any) => {
  return String(action || "").toUpperCase();
};

const formatActionLabel = (action: any) => {
  const value = normalizeAction(action);

  if (value === "ADD") return "Add";
  if (value === "EDIT") return "Edit";
  if (value === "DELETE") return "Delete";

  return action || "-";
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-MY", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const parseJsonValue = (value: any) => {
  if (!value) return null;

  if (typeof value === "object") {
    return value;
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  return value;
};

const renderValue = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "object") {
    return (
      <pre className="audit-json-inline">{JSON.stringify(value, null, 2)}</pre>
    );
  }

  return String(value);
};

const AuditValueBox = ({
  title,
  data,
  emptyText,
}: {
  title: string;
  data: any;
  emptyText: string;
}) => {
  const parsedData = parseJsonValue(data);

  if (!parsedData) {
    return (
      <div className="audit-value-box">
        <h4>{title}</h4>
        <div className="audit-empty-value">{emptyText}</div>
      </div>
    );
  }

  if (typeof parsedData !== "object" || Array.isArray(parsedData)) {
    return (
      <div className="audit-value-box">
        <h4>{title}</h4>
        <pre className="audit-json-block">
          {JSON.stringify(parsedData, null, 2)}
        </pre>
      </div>
    );
  }

  return (
    <div className="audit-value-box">
      <h4>{title}</h4>

      <div className="audit-value-table-wrap">
        <table className="audit-value-table">
          <tbody>
            {Object.entries(parsedData).map(([key, value]) => (
              <tr key={key}>
                <td className="audit-value-key">{key}</td>
                <td className="audit-value-data">{renderValue(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function AuditTrail() {
  const [auditTrails, setAuditTrails] = useState<AuditTrailRecord[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUserOption[]>([]);

  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [viewRecord, setViewRecord] = useState<any | null>(null);

  const [selectedUser, setSelectedUser] = useState("all");
  const [selectedAction, setSelectedAction] = useState("all");
  const [selectedModule, setSelectedModule] = useState("all");

  const [loading, setLoading] = useState(false);

  const fetchAuditTrails = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/admin/audit-trails?limit=5000`, {
        withCredentials: true,
      });

      const result = res.data;

      if (!result.success) {
        setAuditTrails([]);
        return;
      }

      setAuditTrails(getArrayFromResponse(result));
    } catch (error) {
      console.error("fetchAuditTrails error:", error);
      setAuditTrails([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/admin/users`, {
        withCredentials: true,
      });

      const result = res.data;
      setAdminUsers(getArrayFromResponse(result));
    } catch (error) {
      console.error("fetchAdminUsers error:", error);
      setAdminUsers([]);
    }
  };

  useEffect(() => {
    fetchAuditTrails();
    fetchAdminUsers();
  }, []);

  const moduleOptions = useMemo(() => {
    const uniqueModules = Array.from(
      new Set(
        auditTrails
          .map((item) => item.module_name)
          .filter((moduleName) => Boolean(moduleName)),
      ),
    ).sort();

    return [
      { label: "All Modules", value: "all" },
      ...uniqueModules.map((moduleName) => ({
        label: moduleName,
        value: moduleName,
      })),
    ];
  }, [auditTrails]);

  const userOptions = useMemo(() => {
    if (adminUsers.length > 0) {
      return [
        { label: "All Users", value: "all" },
        ...adminUsers.map((user) => ({
          label:
            user.name ||
            user.username ||
            user.email ||
            `Admin User #${user.id}`,
          value: String(user.id),
        })),
      ];
    }

    const fallbackUsers = auditTrails.reduce((acc: any[], item) => {
      if (!item.admin_user_id) return acc;

      const exists = acc.some(
        (user) => String(user.value) === String(item.admin_user_id),
      );

      if (!exists) {
        acc.push({
          value: String(item.admin_user_id),
          label:
            item.admin_name ||
            item.admin_email ||
            `Admin User #${item.admin_user_id}`,
        });
      }

      return acc;
    }, []);

    return [{ label: "All Users", value: "all" }, ...fallbackUsers];
  }, [adminUsers, auditTrails]);

  const preparedAuditTrails = useMemo(() => {
    return auditTrails.map((item) => {
      const createdDate = item.createdAt || item.created_at;

      return {
        ...item,
        created_at_display: formatDateTime(createdDate),
        user_display:
          item.admin_name ||
          item.admin_email ||
          (item.admin_user_id ? `Admin User #${item.admin_user_id}` : "-"),
        action_display: formatActionLabel(item.action),
        record_display:
          item.record_label ||
          item.record_id ||
          `${item.module_name || "Record"} #${item.record_id || item.id}`,
      };
    });
  }, [auditTrails]);

  const filteredAuditTrails = useMemo(() => {
    return preparedAuditTrails.filter((item) => {
      const matchesUser =
        selectedUser === "all" ||
        String(item.admin_user_id || "") === String(selectedUser);

      const matchesAction =
        selectedAction === "all" ||
        normalizeAction(item.action) === selectedAction;

      const matchesModule =
        selectedModule === "all" || item.module_name === selectedModule;

      return matchesUser && matchesAction && matchesModule;
    });
  }, [preparedAuditTrails, selectedUser, selectedAction, selectedModule]);

  const columns: Column[] = [
    {
      key: "id",
      label: "ID",
    },
    {
      key: "created_at_display",
      label: "Date",
    },
    {
      key: "user_display",
      label: "User",
      render: (row: any) => (
        <div className="audit-user-cell">
          <strong>{row.admin_name || row.user_display}</strong>
          {row.admin_email && <span>{row.admin_email}</span>}
        </div>
      ),
    },
    {
      key: "action_display",
      label: "Action",
      render: (row: any) => (
        <span
          className={`audit-action-badge ${normalizeAction(
            row.action,
          ).toLowerCase()}`}
        >
          {formatActionLabel(row.action)}
        </span>
      ),
    },
    {
      key: "module_name",
      label: "Module",
    },
    {
      key: "record_display",
      label: "Record",
    },
    {
      key: "ip_address",
      label: "IP Address",
      render: (row: any) => row.ip_address || "-",
    },
    {
      key: "view",
      label: "View",
      render: (row: any) => (
        <button
          type="button"
          className="audit-view-btn"
          onClick={(e) => {
            e.stopPropagation();
            setViewRecord(row);
          }}
        >
          View
        </button>
      ),
    },
  ];

  const resetFilters = () => {
    setSelectedUser("all");
    setSelectedAction("all");
    setSelectedModule("all");
  };

  return (
    <div className="audit-trail-page">
      <AdminListToolkit
        title="Audit Trail"
        columns={columns}
        data={filteredAuditTrails}
        selectedRow={selectedRow}
        enableExport
        exportFileName="audit-trail"
        extraButtons={[
          {
            label: "Refresh",
            onClick: fetchAuditTrails,
          },
        ]}
      >
        {(searchedData: any) => (
          <>
            <div className="audit-filter-area">
              <div className="audit-filter-group">
                <label>User</label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                >
                  {userOptions.map((user) => (
                    <option key={user.value} value={user.value}>
                      {user.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="audit-filter-group">
                <label>Action</label>
                <select
                  value={selectedAction}
                  onChange={(e) => setSelectedAction(e.target.value)}
                >
                  {ACTION_OPTIONS.map((action) => (
                    <option key={action.value} value={action.value}>
                      {action.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="audit-filter-group">
                <label>Module</label>
                <select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                >
                  {moduleOptions.map((module) => (
                    <option key={module.value} value={module.value}>
                      {module.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="audit-filter-actions">
                <button
                  type="button"
                  className="audit-reset-btn"
                  onClick={resetFilters}
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="audit-count-row">
              {loading ? (
                <span>Loading audit records...</span>
              ) : (
                <span>
                  Showing <strong>{searchedData.length}</strong> audit record(s)
                </span>
              )}
            </div>

            <DataTable
              columns={columns}
              data={searchedData}
              selectedRowId={selectedRow?.id || null}
              onRowSelect={(row) => setSelectedRow(row)}
            />
          </>
        )}
      </AdminListToolkit>

      {viewRecord && (
        <div className="audit-modal-overlay">
          <div className="audit-modal">
            <div className="audit-modal-header">
              <div>
                <h3>Audit Trail Detail</h3>
                <p>
                  {viewRecord.module_name} /{" "}
                  {viewRecord.record_label || viewRecord.record_id || "-"}
                </p>
              </div>

              <button
                type="button"
                className="audit-modal-close"
                onClick={() => setViewRecord(null)}
              >
                ✕
              </button>
            </div>

            <div className="audit-modal-user-header">
              <div className="audit-modal-info-card">
                <span>User</span>
                <strong>{viewRecord.admin_name || "-"}</strong>
                <small>{viewRecord.admin_email || "-"}</small>
              </div>

              <div className="audit-modal-info-card">
                <span>Action</span>
                <strong>
                  <span
                    className={`audit-action-badge ${normalizeAction(
                      viewRecord.action,
                    ).toLowerCase()}`}
                  >
                    {formatActionLabel(viewRecord.action)}
                  </span>
                </strong>
              </div>

              <div className="audit-modal-info-card">
                <span>Module</span>
                <strong>{viewRecord.module_name || "-"}</strong>
              </div>

              <div className="audit-modal-info-card">
                <span>Date & Time</span>
                <strong>
                  {formatDateTime(
                    viewRecord.createdAt || viewRecord.created_at,
                  )}
                </strong>
              </div>

              <div className="audit-modal-info-card">
                <span>IP Address</span>
                <strong>{viewRecord.ip_address || "-"}</strong>
              </div>

              <div className="audit-modal-info-card">
                <span>Record ID</span>
                <strong>{viewRecord.record_id || "-"}</strong>
              </div>
            </div>

            <div className="audit-user-agent">
              <span>User Agent:</span>
              <p>{viewRecord.user_agent || "-"}</p>
            </div>

            <div className="audit-values-grid">
              <AuditValueBox
                title="Old Values"
                data={viewRecord.old_data}
                emptyText="No old value. This was probably an Add action."
              />

              <AuditValueBox
                title="New Values"
                data={viewRecord.new_data}
                emptyText="No new value. This was probably a Delete action."
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
